import React, { useEffect, useRef, useState } from 'react';
import {
  useViewStateStore,
  ViewStateStoreApi
} from '../../stores/viewState.store';
import { Questionnaire } from '../Questionaire/Questionnaire';
import {
  Button,
  copyToClipboard,
  intl,
  LoadMask,
  notifyConfirm
} from 'ehs-react';
import {
  useWaitingRoomStore,
  WaitingRoomStoreApi
} from '../../stores/waitingRoom.store';
import type { at } from '../../generated/Beans';
type Institution = at.sense.portlets.saf.virtualvisitpatient.model.Institution;
import { usePortletStore } from '../../App';
import {
  InviteGuest,
  Participants,
  useHeartBeat,
  useLiferaySessionExtender
} from 'virtual-visit-common';
import { useQuestionnaireStore } from '../../stores/questionnaire.store';
import { ChatWrapper } from '../VideoConf/VideoConference';
import { useChatStore } from '../../stores/chat.store';
import { mapPatientData } from '../../utils/utils';
import { useWebsocketConnectionCheck } from '../../communication/websocket';

export function WaitingRoom(): JSX.Element {
  const [queueId, loggedInPatient, institution] = useViewStateStore(state => [
    state.viewStateData?.queueId,
    state.viewStateData?.loggedInPatient,
    state.viewStateData?.selectedInstitution
  ]);
  const [init] = useState(true);

  useLiferaySessionExtender(30000, init);

  useGuestStatusChangeHandler();

  useWebsocketConnectionCheck();

  const [
    initSession,
    leaveWaitingList,
    inviteGuests,
    sendVisitStatus,
    visitInfo,
    loading,
    waitingRoomTab,
    sessionInitalized,
    participantList,
    recreateGuestLink,
    physicianUser,
    callState,
    guestBeans,
    inviteLinkDrawerOpen,
    toggleInviteLinkDrawerOpen
  ] = useWaitingRoomStore(state => [
    state.initSession,
    state.leaveWaitingList,
    state.inviteGuests,
    state.sendVisitStatus,
    state.visitInfo,
    state.loading,
    state.waitingRoomTab,
    state.sessionInitalized,
    state.participants,
    state.recreateGuestLink,
    state.visitInfo?.visit?.user,
    state.visitInfo?.visit?.status,
    state.guestBeans,
    state.inviteLinkDrawerOpen,
    state.toggleInviteLinkDrawerOpen
  ]);

  console.log('physicianUser', physicianUser);

  const [
    institutionQuestionnaire,
    loadQuestionnaire,
    questionnaireIsLoaded
  ] = useQuestionnaireStore(state => [
    state.institutionQuestionnaire,
    state.loadQuestionnaire,
    state.questionnaireIsLoaded
  ]);

  const [
    mandatoryFieldForRegistration,
    webSocketPingInterval,
    maxGuests
  ] = usePortletStore(state => [
    state.configuration.registrationConfig.mandatoryFieldForRegistration,
    state.configuration.webSocketConfig.webSocketPingInterval,
    state.configuration.maxGuests
  ]);

  const [
    numberUnreadMessage,
    resetUnreadMessages,
    chatMessages
  ] = useChatStore(store => [
    store.numberOfUnreadMessages,
    store.resetUnreadMessages,
    store.chatMessages
  ]);

  const [guestToken] = useViewStateStore(state => [
    state.viewStateData?.guestData?.urlToken
  ]);
  const isGuest = !!guestToken;
  const groupCalls = maxGuests > 0;
  const isMaxGuestLimitReached =
    participantList.filter(p => p.status.startsWith('VALID_')).length >=
    maxGuests;

  const [enableChatWithinWaitingRoom] = usePortletStore(store => [
    store.configuration.enableChatWithinWaitingRoom
  ]);

  useEffect(() => {
    if (sessionInitalized) {
      loadQuestionnaire();
    }
  }, [loadQuestionnaire, sessionInitalized]);

  const [step, setStep] = useState(2);

  const linkNotGenerated =
    participantList.filter(p => p.status.startsWith('VALID_')).length <= 0;
  // we send each 1min status that the visit is active
  // also keeps the websocket alive

  useHeartBeat(sendVisitStatus, webSocketPingInterval);

  const onReject = () => console.log('reject leave waiting room');
  const onConfirm = () => leaveWaitingList();

  const waitingRoomButtonClick = () => {
    if (step < 3 && !isGuest) {
      setStep(step + 1);
    } else {
      console.log('leave waiting room - confirm');
      notifyConfirm({
        message: intl(
          'virtualvisitpatientportlet.waitingroom.leave.warning.question'
        ),
        onConfirm,
        onReject,
        title: intl('virtualvisitpatientportlet.waitingroom.leave.warning')
      });
    }
  };
  const noQuestionnaire = institutionQuestionnaire?.questionnaires.length === 0;
  useEffect(() => {
    if (step === 2 && noQuestionnaire) {
      setStep(step + 1);
    }
  }, [step, setStep, noQuestionnaire]);

  const [disableRejoining] = usePortletStore(store => [
    store.configuration.disableRejoining
  ]);

  useEffect(() => {
    if (!visitInfo) {
      initSession(mandatoryFieldForRegistration);
    }
  }, [visitInfo, initSession, mandatoryFieldForRegistration]);

  let buttonText = intl('common.continue');

  let content = <React.Fragment />;
  const onChatButtonClick = () => {
    WaitingRoomStoreApi.setState({ waitingRoomTab: 'CHAT' });
    resetUnreadMessages();
  };
  let showAccidentalExit = false;
  if (step === 2) {
    buttonText = intl('common.continue');
    showAccidentalExit = true;
  } else if (step === 3) {
    showAccidentalExit = noQuestionnaire && questionnaireIsLoaded;
    buttonText = intl(
      'virtualvisitpatientportlet.waitingroom.leave.button.label'
    );
  }
  const showInviteLink = groupCalls && !isGuest && !isMaxGuestLimitReached;
  content = (
    <>
      <Header institution={institution} />
      <WaitingPosition position={visitInfo?.queueInfoBean.currentPosition} />
      {waitingRoomTab === 'WAITING_ROOM' && (
        <InviteGuest
          visitId={visitInfo?.visitId}
          showInviteLink={showInviteLink}
          inviteGuests={inviteGuests}
          guestBeans={guestBeans}
          inviteLinkDrawerOpen={inviteLinkDrawerOpen}
          toggleInviteLinkDrawerOpen={toggleInviteLinkDrawerOpen}
        />
      )}
      {(step === 3 || isGuest) && (
        <Button
          id="vvpp-leave-waiting-room-button"
          className="vvpp-leave-waiting-room-button"
          variant="gray"
          text={intl(
            'virtualvisitpatientportlet.waitingroom.leave.button.label'
          )}
          onClick={waitingRoomButtonClick}
        />
      )}
      {!loggedInPatient && !disableRejoining && (
        <>
          {showAccidentalExit && <AccidentallyExit isGuest={isGuest} />}
          {!isGuest && <AccessCode queueId={queueId || ''} />}
        </>
      )}
      {!loggedInPatient && disableRejoining && (
        <>
          <NoExitStats />
        </>
      )}
      {!isGuest && <PleaseWait />}
      {!noQuestionnaire && questionnaireIsLoaded && !isGuest && (
        <PleaseAnswerQuestions />
      )}
      {step === 3 && <Questionnaire />}
      {groupCalls && (
        <Participants
          admitDeclineGuest={() =>
            console.log('Patient does not have actions on guest.')
          }
          isReadOnly={true}
          visitId={visitInfo?.visitId}
          patient={mapPatientData(
            ViewStateStoreApi.getState().viewStateData?.patientData
          )}
          participants={participantList}
          physician={physicianUser}
          recreateGuestLink={recreateGuestLink}
          callStatus={callState}
          isGuestView={isGuest}
          invitedBy={'PATIENT'}
        />
      )}
      {enableChatWithinWaitingRoom && !isGuest && (
        <ChatButton
          chatMessagesLength={chatMessages?.length}
          numberUnreadMessage={numberUnreadMessage}
          onChatButtonClicked={onChatButtonClick}
        />
      )}
    </>
  );

  const backButtonClick = () => {
    WaitingRoomStoreApi.setState({ waitingRoomTab: 'WAITING_ROOM' });
  };

  const waitingRoomCls =
    waitingRoomTab === 'WAITING_ROOM' ? '' : ' vvpp-hidden';
  const chatCls = waitingRoomTab === 'CHAT' ? '' : ' vvpp-hidden';

  useEffect(() => {
    if (waitingRoomTab === 'CHAT' && numberUnreadMessage > 0) {
      resetUnreadMessages();
    }
  }, [numberUnreadMessage, resetUnreadMessages, waitingRoomTab]);
  const showContinueButton =
    waitingRoomTab === 'WAITING_ROOM' &&
    step != 3 &&
    !noQuestionnaire &&
    questionnaireIsLoaded &&
    !isGuest;
  return (
    <>
      <LoadMask masked={loading} />
      <div className={'vvpp-content' + waitingRoomCls}>{content}</div>
      <div className={'vvpp-content vvpp-waiting-room-chat' + chatCls}>
        <ChatWrapper onBackButtonClick={backButtonClick} />
      </div>
      {showContinueButton && (
        <div className="vvpp-single-button">
          <Button
            id="vvpp-waiting-room-button"
            variant="primary"
            text={buttonText}
            onClick={waitingRoomButtonClick}
          />
        </div>
      )}
    </>
  );
}

function WaitingPosition(props: { position: number | undefined }): JSX.Element {
  return (
    <div className="vvpp-waiting-position">
      {intl('virtualvisitpatientportlet.waitingroom.position')}{' '}
      <span>#{props.position}</span>
    </div>
  );
}

function AccessCode(props: { queueId: string }): JSX.Element {
  const [codeCopied, setCodeCopied] = useState(false);
  const copyCode = async () => {
    copyToClipboard(props.queueId);
    setCodeCopied(true);
    return false;
  };

  let copyCodeLnk: JSX.Element;
  if (codeCopied) {
    copyCodeLnk = <span className="vvpp-copy-check ehealthicon-check" />;
  } else {
    copyCodeLnk = (
      <a href="#" onClick={copyCode}>
        {intl('virtualvisitpatientportlet.waitingroom.copy.code')}
      </a>
    );
  }

  return (
    <div className="vvpp-please-wait-info vvpp-access-code-container">
      {intl('virtualvisitpatientportlet.waitingroom.your.access.code')}{' '}
      <span className="vvpp-access-code">
        {props.queueId}
        {copyCodeLnk}
      </span>
    </div>
  );
}

function PleaseWait(): JSX.Element {
  return (
    <div className="vvpp-please-wait-info">
      {intl('virtualvisitpatientportlet.waitingroom.please.wait')}
    </div>
  );
}

function ChatButton(props: {
  chatMessagesLength: number;
  numberUnreadMessage: number;
  onChatButtonClicked: () => void;
}): JSX.Element {
  return (
    <div className="vvpp-waiting-room-show-chat-button-container">
      <Button
        id="vvpp-waiting-room-show-chat-button"
        className="vvpp-waiting-room-show-chat-button"
        variant="primary"
        icon="vv-chat-header"
        onClick={props.onChatButtonClicked}
        disabled={props.chatMessagesLength <= 0}
        text={intl('virtualvisitpatientportlet.open.chat.messages')}
      />
      {props.numberUnreadMessage > 0 && (
        <div className="vvpp-waiting-room-chat-notification" />
      )}
    </div>
  );
}

function AccidentallyExit(props: { isGuest?: boolean }): JSX.Element {
  return (
    <div
      className="vvpp-please-wait-info"
      dangerouslySetInnerHTML={{
        __html: props.isGuest
          ? intl('virtualvisitpatientportlet.waitingroom.position.status.guest')
          : intl('virtualvisitpatientportlet.waitingroom.position.status')
      }}
    />
  );
}

function NoExitStats(): JSX.Element {
  return (
    <div
      className="vvpp-please-wait-info"
      dangerouslySetInnerHTML={{
        __html: intl(
          'virtualvisitpatientportlet.waitingroom.position.status.noexit'
        )
      }}
    />
  );
}

function PleaseAnswerQuestions(): JSX.Element {
  return (
    <div className="vvpp-please-wait-info">
      {intl('virtualvisitpatientportlet.waitingroom.please.anwser.questions')}
    </div>
  );
}

function Header(props: { institution?: Institution }): JSX.Element {
  if (!props.institution) {
    return <h2>{intl('virtualvisitpatientportlet.waitingroom.header')}</h2>;
  } else {
    return (
      <div className="vvpp-institution-header">
        {props.institution?.logo && (
          <img
            className="vvpp-header-institution-icon"
            src={`data:image/jpeg;base64,${props.institution?.logo}`}
            alt="Institution logo"
          />
        )}
        <h2 className="vvpp-institution-logo-header-text">
          {props.institution?.name}
        </h2>
      </div>
    );
  }
}

function useGuestStatusChangeHandler() {
  const [currentGuestStatus] = useWaitingRoomStore(store => [
    store.currentGuestParticipant?.status
  ]);
  const isFirstMount = useRef(true);

  useEffect(() => {
    console.log('currentGuestStatus', currentGuestStatus);

    /*if guest state changes to VALID_APPROVED or INVALID_* call next state,
    which either forward to the call (accepted) or to the final screen (rejected)*/
    if (isFirstMount.current && currentGuestStatus) {
      console.log(
        'skipping useGuestStatusChangeHandler',
        'firstMount',
        isFirstMount.current,
        'currentGuestStatus',
        currentGuestStatus
      );
      isFirstMount.current = false;
    } else {
      if (
        currentGuestStatus === 'VALID_APPROVED' ||
        currentGuestStatus?.startsWith('INVALID_')
      ) {
        if (currentGuestStatus?.startsWith('INVALID_')) {
          sessionStorage.clear();
        }
        console.log('trigger next state from WaitingRoom');
        ViewStateStoreApi.getState().nextState();
      }
    }
  }, [currentGuestStatus]);
}
